/**
 * "Blocked: approval expired" (or denied, or refused by policy) beside a session's status, wherever
 * a session is listed (R-HL-3): the drawer header, a mission's session lines. In a Sessions row the
 * block is the row's one status instead (ApprovalBlockMark in the status column), so a row never
 * shows a finished mark and a Blocked pill at once. Icon plus label; renders nothing for a session
 * no decided approval stopped.
 */
import { useMemo } from 'react';
import { Loader, ShieldAlert } from '@orgus/ui/icons';
import { approvalBlockFor, approvalsForDerive } from '@orgus/protocol';
import type { ApprovalBlock, ApprovalsState, Session } from '@orgus/protocol';
import { Badge, cn } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';

/** The badge for a known block. `compact` says just "Blocked" (the full label is its title). */
export function ApprovalBlockBadge({ block, compact, className }: { block: ApprovalBlock; compact?: boolean; className?: string }) {
  if (block.phase === 'resuming') {
    return (
      <Badge tone="working" icon={<Loader />} className={className} title="Approve and resume was sent: the session is continuing.">
        Resuming
      </Badge>
    );
  }
  return (
    <Badge tone="failed" icon={<ShieldAlert />} className={className} title={block.title}>
      {compact ? 'Blocked' : block.label}
    </Badge>
  );
}

/**
 * A block as a row's status mark: the shield on a soft danger disc, the size of the row's agent
 * orb (20 px), so it takes the status column's place. The label is for the row's tooltip and
 * screen readers.
 */
export function ApprovalBlockMark({ block, className }: { block: ApprovalBlock; className?: string }) {
  return (
    <span data-block-mark="" role="img" aria-label={block.label} className={cn('inline-flex size-5 shrink-0 items-center justify-center rounded-pill bg-status-danger-soft text-status-danger', className)}>
      <ShieldAlert className="size-3" strokeWidth={2.25} aria-hidden />
    </span>
  );
}

/** The decided approval that stops this session, or null. Cheap enough for every row: one store read, no timer. */
export function useSessionBlock(session: Session): ApprovalBlock | null {
  const approvals = useOrgus((s) => s.ext.approvals as ApprovalsState | undefined);
  return useMemo(
    () => (approvals && session.status !== 'working' && session.status !== 'waiting' ? approvalBlockFor(session, approvalsForDerive(approvals) ?? [], Date.now()) : null),
    [approvals, session],
  );
}

/** Mount: beside a session's status. */
export function SessionBlockedBadge({ session, compact, className }: { session: Session; compact?: boolean; className?: string }) {
  const block = useSessionBlock(session);
  if (!block) return null;
  return <ApprovalBlockBadge block={block} compact={compact} className={className} />;
}
